import React, {
    useEffect,
    useRef,
    useState,
} from "react";

import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import {
    fetchActorMovieRoleFromWikipedia,
} from "../utils/wikipediaMovieRole";


// ======================================================
// MOVIEFRENZY — RÔLE DANS CE FILM
// Données réelles : TMDB + Wikipédia / MediaWiki
// ======================================================

const MOVIE_ROLE_COPY = {
    fr: {
        trigger: "Rôle dans ce film",
        title: "Rôle dans ce film",
        character: "Personnage",
        role: "Rôle dans le récit",
        story: "Dans l’histoire",
        movie: "Film",
        close: "Fermer le rôle dans ce film",
    },

    en: {
        trigger: "Role in this film",
        title: "Role in this film",
        character: "Character",
        role: "Role in the story",
        story: "In the story",
        movie: "Film",
        close: "Close role in this film",
    },

    es: {
        trigger: "Papel en esta película",
        title: "Papel en esta película",
        character: "Personaje",
        role: "Papel en la historia",
        story: "En la historia",
        movie: "Película",
        close: "Cerrar el papel en esta película",
    },

    de: {
        trigger: "Rolle in diesem Film",
        title: "Rolle in diesem Film",
        character: "Figur",
        role: "Rolle in der Handlung",
        story: "In der Handlung",
        movie: "Film",
        close: "Rolle in diesem Film schließen",
    },
};


// ======================================================
// COMPARAISON QUALITATIVE ROLE / STORY
// Évite d'afficher deux contenus identiques ou quasi identiques.
// ======================================================

const normalizeComparableText = (text = "") =>
    String(text)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\p{L}\p{N}\s]/gu, " ")
        .replace(/\s+/g, " ")
        .trim();


const areTextsTooSimilar = (textA, textB) => {
    const a = normalizeComparableText(textA);
    const b = normalizeComparableText(textB);

    if (!a || !b) {
        return false;
    }

    if (a === b) {
        return true;
    }

    if (
        a.length >= 20 &&
        b.length >= 20 &&
        (a.includes(b) || b.includes(a))
    ) {
        return true;
    }

    const wordsA = new Set(a.split(" "));
    const wordsB = new Set(b.split(" "));

    const commonWords =
        [...wordsA].filter((word) =>
            wordsB.has(word)
        ).length;

    const smallestSetSize =
        Math.min(
            wordsA.size,
            wordsB.size
        );

    if (!smallestSetSize) {
        return false;
    }

    const similarity =
        commonWords / smallestSetSize;

    return similarity >= 0.8;
};


const ActorMovieRolePopover = ({
    actorId,
    actorName,
    movie,
}) => {
    const { currentLanguage } = useLanguage();

    const popoverRef = useRef(null);

    const [
        isPopoverOpen,
        setIsPopoverOpen,
    ] = useState(false);

    const [
        wikipediaRoleData,
        setWikipediaRoleData,
    ] = useState(null);

    const languageKey =
        String(currentLanguage || "en")
            .split("-")[0]
            .toLowerCase();

    const copy =
        MOVIE_ROLE_COPY[languageKey] ||
        MOVIE_ROLE_COPY.en;


    // ======================================================
    // WIKIPÉDIA — GÉNÉRALISÉ À TOUS LES ACTEURS / FILMS
    // ======================================================

    useEffect(() => {
        setWikipediaRoleData(null);

        if (!movie?.id || !actorName) {
            return undefined;
        }

        let cancelled = false;

        const fetchWikipediaRole = async () => {
            try {
                const result =
                    await fetchActorMovieRoleFromWikipedia({
                        movie,
                        actorName,
                        language: languageKey,
                    });

                if (!cancelled) {
                    console.log(
                        "🎬 MovieFrenzy Wikipedia role:",
                        result
                    );

                    setWikipediaRoleData(result);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error(
                        "❌ Wikipedia role error:",
                        error
                    );

                    setWikipediaRoleData(null);
                }
            }
        };

        fetchWikipediaRole();

        return () => {
            cancelled = true;
        };
    }, [
        actorName,
        movie?.id,
        movie?.title,
        movie?.original_title,
        movie?.release_date,
        languageKey,
    ]);


    const popoverId =
        `actor-movie-role-${actorId || "person"}-${movie?.id || "movie"}`;


    // Synchronise React avec l'état réel du Popover API.
    useEffect(() => {
        const popover =
            popoverRef.current;

        if (!popover) {
            return undefined;
        }

        const handleToggle = (event) => {
            setIsPopoverOpen(
                event.newState === "open"
            );
        };

        popover.addEventListener(
            "toggle",
            handleToggle
        );

        return () => {
            popover.removeEventListener(
                "toggle",
                handleToggle
            );
        };
    }, []);


    // Si le film change pendant qu'un popover
    // est ouvert, on le ferme proprement.
    useEffect(() => {
        setIsPopoverOpen(false);

        const popover =
            popoverRef.current;

        if (
            popover &&
            typeof popover.hidePopover === "function" &&
            popover.matches?.(":popover-open")
        ) {
            popover.hidePopover();
        }
    }, [movie?.id]);


    const openPopover = () => {
        const popover =
            popoverRef.current;

        if (
            popover &&
            typeof popover.showPopover === "function"
        ) {
            popover.showPopover();
        }
    };


    const closePopover = () => {
        const popover =
            popoverRef.current;

        if (
            popover &&
            typeof popover.hidePopover === "function"
        ) {
            popover.hidePopover();
        }
    };


    // ======================================================
    // DONNÉES RÉELLES
    //
    // 1. TMDB fournit le personnage via movie.character.
    // 2. Wikipédia peut enrichir / corriger le personnage.
    // 3. Wikipédia peut ajouter rôle narratif + histoire.
    // ======================================================

    const hasWikipediaData =
        wikipediaRoleData?.found === true;

    const tmdbCharacter =
        String(movie?.character || "").trim();

    const wikipediaCharacter =
        hasWikipediaData
            ? String(
                wikipediaRoleData?.character || ""
            ).trim()
            : "";

    const character =
        wikipediaCharacter ||
        tmdbCharacter ||
        null;

    const story =
        hasWikipediaData &&
            wikipediaRoleData?.story
            ? String(
                wikipediaRoleData.story
            ).trim() || null
            : null;

    const rawWikipediaRole =
        hasWikipediaData &&
            wikipediaRoleData?.role
            ? String(
                wikipediaRoleData.role
            ).trim() || null
            : null;

    const wikipediaRoleAndStoryAreTooSimilar =
        Boolean(
            rawWikipediaRole &&
            story &&
            areTextsTooSimilar(
                rawWikipediaRole,
                story
            )
        );

    const role =
        wikipediaRoleAndStoryAreTooSimilar
            ? null
            : rawWikipediaRole;

    const hasDisplayableData =
        Boolean(
            character ||
            role ||
            story
        );


    // Rien à afficher tant qu'aucune donnée réellement
    // exploitable n'est disponible pour ce film.
    if (!movie || !hasDisplayableData) {
        return null;
    }


    const movieTitle =
        movie?.title ||
        movie?.original_title ||
        copy.movie;

    const movieYear =
        movie?.release_date
            ? movie.release_date.slice(0, 4)
            : null;

    const moviePosterUrl =
        movie?.poster_path
            ? `https://image.tmdb.org/t/p/w185${movie.poster_path}`
            : null;

    const wikipediaSourceUrl =
        hasWikipediaData &&
            wikipediaRoleData?.sourceUrl
            ? wikipediaRoleData.sourceUrl
            : null;


    return (
        <>
            {/* ===============================
                BOUTON DÉCLENCHEUR
            =============================== */}

            <button
                type="button"
                className="actor-movie-role-trigger"
                aria-controls={popoverId}
                aria-expanded={isPopoverOpen}
                aria-haspopup="dialog"
                onClick={openPopover}
            >
                <span
                    className="actor-movie-role-trigger__icon"
                    aria-hidden="true"
                >
                    🎭
                </span>

                <span className="actor-movie-role-trigger__label">
                    {copy.trigger}
                </span>
            </button>


            {/* ===============================
                POPOVER
            =============================== */}

            <div
                ref={popoverRef}
                id={popoverId}
                className="actor-movie-role-popover"
                popover="auto"
                role="dialog"
                aria-labelledby={`${popoverId}-title`}
            >

                {/* HEADER */}

                <header className="actor-movie-role-popover__header">

                    <div className="actor-movie-role-popover__heading">

                        <span className="actor-movie-role-popover__eyebrow">
                            🎭 MovieFrenzy
                        </span>

                        <h2 id={`${popoverId}-title`}>
                            {copy.title}
                        </h2>

                        <p className="actor-movie-role-popover__actor">
                            {actorName}
                        </p>

                    </div>


                    <div className="actor-movie-role-popover__actions">

                        <button
                            type="button"
                            className="actor-movie-role-popover__close"
                            onClick={closePopover}
                            aria-label={copy.close}
                        >
                            ×
                        </button>

                        {moviePosterUrl && (
                            <Link
                                to={`/ficheFilm/${movie.id}`}
                                className="actor-movie-role-popover__poster-link"
                                onClick={closePopover}
                                aria-label={`${copy.movie} : ${movieTitle}`}
                                title={movieTitle}
                            >
                                <img
                                    src={moviePosterUrl}
                                    alt=""
                                    className="actor-movie-role-popover__poster"
                                />
                            </Link>
                        )}

                    </div>

                </header>


                {/* FILM */}

                <section className="actor-movie-role-film">

                    <span
                        className="actor-movie-role-film__icon"
                        aria-hidden="true"
                    >
                        🎬
                    </span>

                    <div>
                        <small>
                            {copy.movie}
                        </small>

                        <strong>
                            {movieTitle}
                            {movieYear && ` — ${movieYear}`}
                        </strong>
                    </div>

                </section>


                {/* PERSONNAGE */}

                {character && (
                    <section className="actor-movie-role-section">

                        <h3>
                            <span aria-hidden="true">
                                🎭
                            </span>

                            {copy.character}
                        </h3>

                        <p className="actor-movie-role-character">
                            {character}
                        </p>

                    </section>
                )}


                {/* RÔLE */}

                {role && (
                    <section className="actor-movie-role-section">

                        <h3>
                            <span aria-hidden="true">
                                🎯
                            </span>

                            {copy.role}
                        </h3>

                        <p>
                            {role}
                        </p>

                    </section>
                )}


                {/* IMPLICATION DANS L'HISTOIRE */}

                {story && (
                    <section className="actor-movie-role-section">

                        <h3>
                            <span aria-hidden="true">
                                📖
                            </span>

                            {copy.story}
                        </h3>

                        <p className="actor-movie-role-story">
                            {story}
                        </p>

                    </section>
                )}


                {/* SOURCE WIKIPÉDIA RÉELLE, SI DISPONIBLE */}

                {wikipediaSourceUrl && (
                    <a
                        className="actor-movie-role-wikipedia-source"
                        href={wikipediaSourceUrl}
                        target="_blank"
                        rel="noreferrer"
                    >
                        Wikipédia ↗
                    </a>
                )}

            </div>
        </>
    );
};


export default ActorMovieRolePopover;